import React, { Fragment, useCallback, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import ReactModal from "react-modal";
import moment from "moment-timezone";
import Select from "react-select";

import currencyFormatter from "../../helpers/currencyFormatter";
import { LoadingButton } from "../shared/loading-button";

const targetingPaymentsOptions = [
  { label: "None", value: "" },
  { label: "Clients", value: "clients" },
  { label: "Stripe", value: "stripe" },
];

const formatBalance = (amount) =>
  typeof amount === "number" ? currencyFormatter.format(amount) : "-";

const getPendingBalance = (balance) =>
  typeof balance?.current === "number" && typeof balance?.available === "number"
    ? balance.current - balance.available
    : undefined;

const StripeAccountRow = ({ account, disabled, handleModifyAccount }) => {
  const handleChangeTargetingPayments = useCallback((option) => {
    handleModifyAccount(account._id, { targetingPayments: option?.value || null });
  }, [account, handleModifyAccount]);

  const handleDisable = useCallback((event) => {
    event.stopPropagation();

    const { checked } = event.target;

    handleModifyAccount(account._id, { disabled: !checked });
  }, [account, handleModifyAccount]);

  if (!account) {
    return null;
  }

  const balance = account.balances?.[0];

  return (
    <TableRow
      sx={{
        backgroundColor: disabled || account.disabled ? "#ffebee !important" : undefined,
      }}
      className="fm-l2-child-table-head-row"
    >
      <TableCell>{account.mask || "-"}</TableCell>
      <TableCell>{account.name}</TableCell>
      <TableCell>{formatBalance(balance?.available)}</TableCell>
      <TableCell>{formatBalance(getPendingBalance(balance))}</TableCell>
      <TableCell>{formatBalance(balance?.current)}</TableCell>
      <TableCell>
        <Select
          options={targetingPaymentsOptions}
          placeholder="Select"
          name={`targetingPayments[${account._id}]`}
          value={targetingPaymentsOptions.find((option) => option.value === account.targetingPayments) || targetingPaymentsOptions[0]}
          onChange={handleChangeTargetingPayments}
          menuPosition="fixed"
          menuPortalTarget={document.body}
          closeMenuOnSelect
          isDisabled={disabled}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={!disabled && !account.disabled}
          onChange={handleDisable}
          name={`disabled[${account._id}]`}
          disabled={disabled}
        />
      </TableCell>
    </TableRow>
  );
};

const StripeConnectionRow = ({
  connection,
  handleModifyAccount,
  handleDisconnect,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback((event) => {
    event.stopPropagation();

    setIsOpen(open => !open);
  }, []);

  const onDisconnect = useCallback((event) => {
    event.stopPropagation();

    handleDisconnect(connection._id);
  }, [connection, handleDisconnect]);

  if (!connection) {
    return null;
  }

  return (
    <Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: connection.disabled ? "#ffebee !important" : undefined,
        }}
        onClick={toggleOpen}
        className="fm-l2-parent-table-row"
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={toggleOpen}
            className="p-0"
          >
            {isOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
          </IconButton>
        </TableCell>
        <TableCell>
          {moment(connection.createdAt).format("M/D/YY")}
        </TableCell>
        <TableCell>{connection.organizationName}</TableCell>
        <TableCell className="text-align-right">
          {connection.disabled ? "Disconnected" : (
            <button
              className="base-button icon-button icon-button-small btn-danger"
              onClick={onDisconnect}
              title="Disconnect"
              disabled={connection.disabled}
            >
              <span className="material-icons">link_off</span>
            </button>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table aria-label="collapsible table">
                <TableHead className="fm-l2-child-table-head dynamic-accordion-table-header">
                  <TableRow className="fm-l2-child-table-head-row">
                    <TableCell>
                      Account Number
                    </TableCell>
                    <TableCell>
                      Name
                    </TableCell>
                    <TableCell>
                      Available Balance
                    </TableCell>
                    <TableCell>
                      Pending Balance
                    </TableCell>
                    <TableCell>
                      Total Balance
                    </TableCell>
                    <TableCell>
                      Targeting Payments
                    </TableCell>
                    <TableCell>
                      Enabled
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody className="fm-l2-child-table-body">
                  {!connection.accounts?.length && (
                    <TableRow
                      className="fm-l2-parent-table-row"
                    >
                      <TableCell colSpan={7}>No accounts available</TableCell>
                    </TableRow>
                  )}
                  {connection.accounts?.length > 0 && connection.accounts.map((account) =>
                    <StripeAccountRow
                      key={account._id}
                      account={account}
                      disabled={connection.disabled}
                      handleModifyAccount={handleModifyAccount}
                    />,
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

const StripeAccountsTable = ({
  data,
  handleModifyAccount,
  handleDisconnect,
}) => {
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const onDisconnect = useCallback((connectionId) => {
    setIsDisconnectModalOpen(true);
    setSelectedConnectionId(connectionId);
  }, []);

  const handleDisconnectConfirm = useCallback(async () => {
    setIsSaving(true);
    await handleDisconnect(selectedConnectionId);
    setIsSaving(false);
    setIsDisconnectModalOpen(false);
    setSelectedConnectionId(null);
  }, [selectedConnectionId, handleDisconnect]);

  const handleDisconnectCancel = useCallback(() => {
    setIsDisconnectModalOpen(false);
    setSelectedConnectionId(null);
  }, []);

  return (
    <Fragment>
      <TableContainer
        component={Paper}
        className="accordion-table-with-details shadow-none"
      >
        <Table aria-label="collapsible table" data-testid="stripe-accounts-table">
          <TableHead className="fm-l2-parent-table-head dynamic-accordion-table-header">
            <TableRow className="fm-l2-parent-table-head-row">
              <TableCell/>
              <TableCell>
                Linked Date
              </TableCell>
              <TableCell>
                Organization Name
              </TableCell>
              <TableCell className="text-align-right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody className="fm-l2-parent-table-body">
            {!data.length && (
              <TableRow
                className="fm-l2-parent-table-row"
              >
                <TableCell colSpan={4}>No accounts linked yet</TableCell>
              </TableRow>
            )}

            {data.length > 0 && data.map((connection) => (
              <StripeConnectionRow
                key={connection._id}
                connection={connection}
                handleModifyAccount={handleModifyAccount}
                handleDisconnect={onDisconnect}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReactModal
        isOpen={isDisconnectModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleDisconnectCancel}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "calc(min(500px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>
            Disconnect Stripe Account
          </h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            You are about to disconnect this Stripe account from the system. Its sub-accounts will stop being ingested.
            This action cannot be undone. Are you sure you want to continue?
          </div>
        </div>

        <div className="modal-footer w-100 mt-20 d-flex gap-20">
          <button
            className="base-button evo-blue flex-half"
            type="button"
            onClick={handleDisconnectCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <LoadingButton
            isLoading={isSaving}
            className="base-button btn-danger flex-half"
            type="button"
            onClick={handleDisconnectConfirm}
          >
            Disconnect Account
          </LoadingButton>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default StripeAccountsTable;
